import { AnchorIds, TourIds, TourSignal, TourStep, PluginsQuickstartTourVariations } from "../TourTypes";
import { ArrowPosition } from "../StickyNote";
import { StickyButton } from "../StickyButton";
import { useStickyTourStore } from "../../../stores/stickyTourStore";
import { useOnboardingStore } from "../../../stores/onboardingStore";
import { PluginsWriteRewriteBridgeContent, PasteWorkStepContent } from "../TourContent";

const MIN_SELECTION_WORDS = 40;

const TextSelectionCountdown = () => {
  const selectedWordCount = useStickyTourStore((state) => state.selectedWordCount);
  const remaining = MIN_SELECTION_WORDS - selectedWordCount;

  if (selectedWordCount === 0) {
    return <p>Select some text in your document first — your plugin works on highlighted text.</p>;
  }

  if (remaining > 0) {
    return (
      <p>
        Select <strong>{remaining}</strong> more {remaining === 1 ? "word" : "words"}…
      </p>
    );
  }

  return <p>Great — now let's run a plugin on it!</p>;
};

const ImportStepContent = () => {
  const sendFlowSignal = useStickyTourStore((state) => state.sendFlowSignal);
  return (
    <>
      <p>You can import your Work in Progress any time here.</p>
      <StickyButton
        text="OK"
        variant="go-button"
        size="small"
        onClick={() => sendFlowSignal(TourSignal.STICKY_NEXT)}
      />
    </>
  );
};

const PluginsFinalContentImport = () => {
  const sendFlowSignal = useStickyTourStore((state) => state.sendFlowSignal);
  return (
    <>
      <p>
        🎉 Now you're ready to get feedback with <strong>Plugins!</strong> When your import finishes, we'll let you know!
      </p>
      <StickyButton
        text="Got it"
        variant="go-button"
        size="small"
        onClick={() => sendFlowSignal(TourSignal.STICKY_NEXT)}
      />
    </>
  );
};

const PluginsFinalContentNoImport = () => {
  const sendFlowSignal = useStickyTourStore((state) => state.sendFlowSignal);
  return (
    <div className="mt-[55px]">
      <p>Keep testing here or import your project on the homescreen. </p>
      <StickyButton
        text="Got it"
        variant="go-button"
        size="small"
        onClick={() => sendFlowSignal(TourSignal.STICKY_NEXT)}
      />
    </div>
  );
};

const PluginResultsStepContent = () => {
  const sendFlowSignal = useStickyTourStore((state) => state.sendFlowSignal);
  return (
    <>
      <p>Plugin results appear in the right bar under history.</p>
      <StickyButton
        text="OK"
        variant="go-button"
        size="small"
        onClick={() => sendFlowSignal(TourSignal.STICKY_NEXT)}
        className="translate-x-[50%]"
      />
    </>
  );
};

const RunCustomPluginStepContent = () => {
  const generatedPluginName = useOnboardingStore((state) => state.generatedPlugin?.name?.trim());
  const pluginLabel = generatedPluginName || "your plugin";
  return (
    <p>
      Click <strong>{pluginLabel}</strong> to give it a try!
    </p>
  );
};

const getFirstStepByVariation = (variation: PluginsQuickstartTourVariations): TourStep[] => {
  switch (variation) {
    case PluginsQuickstartTourVariations.IMPORT:
      return [
        {
          id: "1",
          stickies: [
            {
              content: "Click into this project to try out your plugin!",
              offsetX: 200,
              offsetY: -50,
              anchorId: AnchorIds.PROJECT_CARD,
              rotation: 3,
              arrowPosition: ArrowPosition.BottomLeft,
            },
          ],
          listeningFor: [TourSignal.PROJECT_CARD_CLICKED],
        },
      ];

    // to do: we're gonna remove this at some point
    case PluginsQuickstartTourVariations.FROM_HELP_MENU:
      return [
        {
          id: "1",
          stickies: [
            {
              content: (
                <p>
                  👋 Welcome to the Plugins tour! Click on <strong>any project</strong> to get started.
                </p>
              ),
              offsetX: -100,
              offsetY: -25,
              anchorId: AnchorIds.LOGO,
              rotation: -5,
              arrowPosition: ArrowPosition.Bottom,
            },
          ],
          listeningFor: [TourSignal.PROJECT_CARD_CLICKED],
        },
      ];

    case PluginsQuickstartTourVariations.NO_IMPORT:
    default:
      return [
        {
          id: "0",
          stickies: [
            {
              content: <ImportStepContent />,
              offsetX: -100,
              offsetY: 30,
              anchorId: AnchorIds.IMPORT_NOVEL_BUTTON,
              rotation: -2,
              arrowPosition: ArrowPosition.Top,
            },
          ],
          listeningFor: [TourSignal.STICKY_NEXT],
          skipSignals: { [TourSignal.PROJECT_CARD_CLICKED]: "1.1" },
        },
        {
          id: "1",
          stickies: [
            {
              content: "Click into this project to try out your plugins!",
              offsetX: 200,
              offsetY: -50,
              anchorId: AnchorIds.PROJECT_CARD,
              rotation: 3,
              arrowPosition: ArrowPosition.BottomLeft,
            },
          ],
          listeningFor: [TourSignal.PROJECT_CARD_CLICKED],
        },
      ];
  }
};

export const getPluginsQuickstartTour = (variation: PluginsQuickstartTourVariations) => {
  const firstSteps = getFirstStepByVariation(variation);

  const pasteStep: TourStep = {
    id: "1.1",
    stickies: [
      {
        content: <PasteWorkStepContent />,
        offsetX: -250,
        offsetY: 50,
        anchorId: AnchorIds.STORY_BIBLE_DRAFT_ICON,
        rotation: -2,
        arrowPosition: ArrowPosition.BottomLeft,
      },
    ],
    listeningFor: [TourSignal.STICKY_NEXT],
  };

  const middleSteps: TourStep[] = [
    {
      id: "1.5",
      stickies: [
        {
          content: <TextSelectionCountdown />,
          offsetX: 0,
          offsetY: 100,
          anchorId: AnchorIds.EDITOR,
          rotation: 3,
          arrowPosition: ArrowPosition.Bottom,
        },
      ],
      listeningFor: [TourSignal.TEXT_SELECTED],
    },
    {
      id: "2",
      stickies: [
        {
          content: (
            <p>
              Click <strong>More Tools</strong> to see your plugins.
            </p>
          ),
          offsetX: -50,
          offsetY: 50,
          anchorId: AnchorIds.MORE_TOOLS_BUTTON,
          rotation: -2,
          arrowPosition: ArrowPosition.Top,
        },
      ],
      listeningFor: [TourSignal.MORE_TOOLS_CLICKED],
    },
    {
      id: "3",
      stickies: [
        {
          content: <RunCustomPluginStepContent />,
          offsetX: -270,
          offsetY: 0,
          anchorId: AnchorIds.CUSTOM_PLUGIN_BUTTON,
          rotation: 4,
          arrowPosition: ArrowPosition.TopRight,
        },
      ],
      listeningFor: [TourSignal.PLUGIN_CLICKED],
    },
    {
      id: "3.1",
      stickies: [
        {
          content: <PluginResultsStepContent />,
          offsetX: -250,
          offsetY: 200,
          anchorId: AnchorIds.RIGHT_BAR,
          rotation: -3,
          arrowPosition: ArrowPosition.TopRight,
        },
      ],
      listeningFor: [TourSignal.STICKY_NEXT],
    },
  ];

  const finalStep: TourStep =
    variation === PluginsQuickstartTourVariations.NO_IMPORT
      ? {
          id: "4",
          stickies: [
            {
              content: <PluginsFinalContentNoImport />,
              offsetX: 50,
              offsetY: 362,
              anchorId: AnchorIds.LEFT_BAR,
              rotation: -5,
            },
            {
              content: (
                <div className="mt-[55px]">
                  <p>
                    🎉 Now you're ready to get feedback with <strong>Plugins!</strong>
                  </p>
                </div>
              ),
              offsetX: 20,
              offsetY: 162,
              anchorId: AnchorIds.LEFT_BAR,
              rotation: 3,
            },
          ],
          listeningFor: [TourSignal.STICKY_NEXT],
        }
      : {
          id: "4",
          stickies: [
            {
              content: <PluginsFinalContentImport />,
              offsetX: 20,
              offsetY: 200,
              anchorId: AnchorIds.LEFT_BAR,
              rotation: -3,
            },
          ],
          listeningFor: [TourSignal.STICKY_NEXT],
        };

  const bridgeStep: TourStep = {
    id: "5",
    stickies: [
      {
        content: <PluginsWriteRewriteBridgeContent />,
        offsetX: -250,
        offsetY: 200,
        anchorId: AnchorIds.RIGHT_BAR,
        rotation: -3,
      },
    ],
    listeningFor: [TourSignal.STICKY_NEXT],
  };

  return {
    id: TourIds.PLUGINS_QUICKSTART,
    variation,
    steps: [...firstSteps, pasteStep, ...middleSteps, finalStep, bridgeStep],
  };
};
